import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Trophy, Loader2, Users, Send, CheckCircle2, Wallet, Clock, CalendarRange, X } from 'lucide-react';
import { useStatsBlockGuard } from '../hooks/useStatsBlockGuard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCA = (n) => `${(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

// Build a [from, to] ISO range (YYYY-MM-DD, end-of-day exclusive on the
// caller side) for the requested preset. All values are computed in the
// user's local timezone, then truncated to the day.
function rangeFor(preset) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fmt = (d) => {
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${m}-${day}`;
    };
    if (preset === 'today') {
        return { from: fmt(today), to: fmt(today) };
    }
    if (preset === 'yesterday') {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        return { from: fmt(y), to: fmt(y) };
    }
    if (preset === 'week') {
        // Monday → today
        const d = today.getDay(); // 0=sun, 1=mon…
        const offset = d === 0 ? 6 : d - 1;
        const monday = new Date(today); monday.setDate(monday.getDate() - offset);
        return { from: fmt(monday), to: fmt(today) };
    }
    if (preset === 'month') {
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: fmt(first), to: fmt(today) };
    }
    return null; // custom — caller manages the dates
}

const PRESET_BUTTONS = [
    { key: 'today', label: "Aujourd'hui" },
    { key: 'yesterday', label: 'Hier' },
    { key: 'week', label: 'Cette semaine' },
    { key: 'month', label: 'Ce mois' },
    { key: 'custom', label: 'Personnalisé' },
];

export default function StatistiquesLeaderboard() {
    useStatsBlockGuard('stats_leaderboard');
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    // Period filter — defaults to "Ce mois" so the screen looks the same
    // as before on first load. The buttons + custom inputs control the
    // date_from / date_to params sent to the backend.
    const [preset, setPreset] = useState('month');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    // Drill-down panel : list of envoyés / acceptés clients for a user.
    const [drillUser, setDrillUser] = useState(null); // { user_id, user_name, user_role }
    const [drillData, setDrillData] = useState(null);
    const [drillLoading, setDrillLoading] = useState(false);
    const [drillTab, setDrillTab] = useState('envoyes');
    // Admin actions : list of assignable users (cached) + currently-open
    // reassign row id so the inline picker can stay focused.
    const [assignables, setAssignables] = useState([]);
    const [reassignOpenId, setReassignOpenId] = useState(null);

    useEffect(() => {
        axios.get(`${API_URL}/api/users/assignable`, { withCredentials: true })
            .then(r => setAssignables(r.data || []))
            .catch(() => setAssignables([]));
    }, []);

    const activeRange = useMemo(() => {
        if (preset === 'custom') {
            return { from: customFrom, to: customTo };
        }
        return rangeFor(preset);
    }, [preset, customFrom, customTo]);

    useEffect(() => {
        // For "custom" we wait until both dates are filled to avoid spamming the API.
        if (preset === 'custom' && (!customFrom || !customTo)) return;
        const params = new URLSearchParams();
        if (activeRange?.from) params.append('date_from', activeRange.from);
        if (activeRange?.to) params.append('date_to', activeRange.to);
        setLoading(true);
        axios.get(`${API_URL}/api/statistiques/leaderboard-commercial?${params}`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [preset, customFrom, customTo, activeRange]);

    // Drill-down loader. Triggered when the admin clicks a leaderboard row.
    // Re-fetches whenever the period changes so the modal stays in sync.
    const openDrill = (row) => {
        setDrillUser({ user_id: row.user_id, user_name: row.user_name, user_role: row.user_role });
        setDrillTab('envoyes');
    };
    const closeDrill = () => { setDrillUser(null); setDrillData(null); };

    useEffect(() => {
        if (!drillUser) return;
        if (preset === 'custom' && (!customFrom || !customTo)) return;
        const params = new URLSearchParams();
        if (activeRange?.from) params.append('date_from', activeRange.from);
        if (activeRange?.to) params.append('date_to', activeRange.to);
        setDrillLoading(true);
        axios.get(
            `${API_URL}/api/statistiques/leaderboard-commercial/${drillUser.user_id}/clients?${params}`,
            { withCredentials: true },
        )
            .then((r) => setDrillData(r.data))
            .catch(() => setDrillData(null))
            .finally(() => setDrillLoading(false));
    }, [drillUser, preset, customFrom, customTo, activeRange]);

    // Refresh both the drill-down list AND the underlying leaderboard so
    // KPIs/podium/total row immediately reflect an admin action.
    const refreshAll = async () => {
        const params = new URLSearchParams();
        if (activeRange?.from) params.append('date_from', activeRange.from);
        if (activeRange?.to) params.append('date_to', activeRange.to);
        try {
            const [lb, drill] = await Promise.all([
                axios.get(`${API_URL}/api/statistiques/leaderboard-commercial?${params}`, { withCredentials: true }),
                drillUser ? axios.get(
                    `${API_URL}/api/statistiques/leaderboard-commercial/${drillUser.user_id}/clients?${params}`,
                    { withCredentials: true },
                ) : Promise.resolve(null),
            ]);
            setData(lb.data);
            if (drill) setDrillData(drill.data);
        } catch { /* silent */ }
    };

    const handleCancel = async (c) => {
        if (!window.confirm(`Annuler le devis de "${c.client_name || 'ce client'}" ? Il passera en statut "Refusé" et ne comptera plus dans le podium/CA.`)) return;
        try {
            await axios.post(
                `${API_URL}/api/statistiques/leaderboard-commercial/devis/${c.devis_id}/cancel`,
                {},
                { withCredentials: true },
            );
            await refreshAll();
        } catch (err) {
            window.alert(err?.response?.data?.detail || 'Erreur lors de l\'annulation');
        }
    };

    const handleReassign = async (c, newUserId) => {
        if (!newUserId) return;
        try {
            await axios.post(
                `${API_URL}/api/statistiques/leaderboard-commercial/devis/${c.devis_id}/reassign`,
                { user_id: newUserId === 'auto' ? null : newUserId },
                { withCredentials: true },
            );
            setReassignOpenId(null);
            await refreshAll();
        } catch (err) {
            window.alert(err?.response?.data?.detail || 'Erreur lors de la réassignation');
        }
    };

    const rows = data?.rows || [];
    const totals = data?.totals || {};
    const podium = data?.podium || [];

    const KPI = ({ label, value, icon: Icon, color }) => (
        <Card className="shadow-sm">
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase text-gray-500 tracking-wide">{label}</p>
                        <p className="text-2xl font-bold text-[#1B365D] mt-1">{value}</p>
                    </div>
                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
                        <Icon className="h-5 w-5" style={{ color }} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <Layout>
            <div className="space-y-6 animate-fadeIn" data-testid="leaderboard-page">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/statistiques')} className="h-8 w-8 p-0" data-testid="lb-back-btn">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="h-10 w-10 rounded-full bg-[#C9A962]/15 flex items-center justify-center">
                        <Trophy className="h-5 w-5 text-[#C9A962]" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Leaderboard commercial
                        </h1>
                        <p className="text-gray-500 mt-0.5 text-sm">Performance par commercial</p>
                    </div>
                </div>

                {/* Period filter — pills + optional custom date range */}
                <Card className="shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <CalendarRange className="w-4 h-4 text-[#1B365D]/60 mr-1" />
                            {PRESET_BUTTONS.map(b => (
                                <Button
                                    key={b.key}
                                    type="button"
                                    size="sm"
                                    variant={preset === b.key ? 'default' : 'outline'}
                                    onClick={() => setPreset(b.key)}
                                    className={`h-8 text-xs ${preset === b.key ? 'bg-[#1B365D] hover:bg-[#112240]' : ''}`}
                                    data-testid={`lb-preset-${b.key}`}
                                >
                                    {b.label}
                                </Button>
                            ))}
                            {preset === 'custom' && (
                                <div className="flex items-center gap-2 ml-2">
                                    <Input
                                        type="date"
                                        value={customFrom}
                                        onChange={(e) => setCustomFrom(e.target.value)}
                                        className="h-8 text-xs w-36"
                                        data-testid="lb-custom-from"
                                    />
                                    <span className="text-xs text-gray-400">→</span>
                                    <Input
                                        type="date"
                                        value={customTo}
                                        onChange={(e) => setCustomTo(e.target.value)}
                                        className="h-8 text-xs w-36"
                                        data-testid="lb-custom-to"
                                    />
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#1B365D]" /></div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPI label="Prospects" value={totals.prospects || 0} icon={Users} color="#1B365D" />
                            <KPI label="Devis envoyés" value={totals.devis_envoyes || 0} icon={Send} color="#0EA5E9" />
                            <KPI label="Devis acceptés" value={totals.devis_acceptes || 0} icon={CheckCircle2} color="#10B981" />
                            <KPI label="CA total" value={fmtCA(totals.ca)} icon={Wallet} color="#C9A962" />
                        </div>

                        {/* Podium */}
                        {podium.length > 0 && (
                            <Card className="shadow-sm">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg flex items-center gap-2 text-[#1B365D]">
                                        <Trophy className="w-5 h-5 text-[#C9A962]" /> Podium {preset === 'today' ? "d'aujourd'hui" : preset === 'yesterday' ? "d'hier" : preset === 'week' ? 'de la semaine' : preset === 'month' ? 'du mois' : 'de la période'}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {podium.map((p, i) => {
                                            const colors = ['#C9A962', '#94A3B8', '#B45309'];
                                            const emoji = ['🥇', '🥈', '🥉'][i];
                                            return (
                                                <div key={p.user_id} className="rounded-xl border-2 p-4 flex items-center gap-3" style={{ borderColor: colors[i] }}>
                                                    <div className="text-3xl">{emoji}</div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="font-semibold text-[#1B365D] truncate">{p.user_name}</p>
                                                        <p className="text-xs text-gray-500 truncate">{p.user_role}</p>
                                                        <p className="text-sm mt-1"><span className="font-bold text-[#C9A962]">{fmtCA(p.ca)}</span> · {p.devis_acceptes} acceptés</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Tableau détaillé */}
                        <Card className="shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg flex items-center gap-2 text-[#1B365D]">
                                    <Users className="w-5 h-5" /> Détail par commercial
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {rows.length === 0 ? (
                                    <p className="text-center text-gray-400 py-8">Aucune donnée</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm" data-testid="lb-table">
                                            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                                                <tr>
                                                    <th className="px-4 py-3 text-left">Commercial</th>
                                                    <th className="px-4 py-3 text-right">Prospects</th>
                                                    <th className="px-4 py-3 text-right">Devis envoyés</th>
                                                    <th className="px-4 py-3 text-right">Acceptés</th>
                                                    <th className="px-4 py-3 text-right">Taux</th>
                                                    <th className="px-4 py-3 text-right">CA</th>
                                                    <th className="px-4 py-3 text-right">Délai envoi</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {rows.map((r) => (
                                                    <tr
                                                        key={r.user_id}
                                                        className="hover:bg-[#1B365D]/5 cursor-pointer transition-colors"
                                                        onClick={() => openDrill(r)}
                                                        data-testid={`lb-row-${r.user_id}`}
                                                    >
                                                        <td className="px-4 py-3">
                                                            <p className="font-medium text-[#1B365D]">{r.user_name}</p>
                                                            <p className="text-xs text-gray-400">{r.user_role}</p>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">{r.prospects}</td>
                                                        <td className="px-4 py-3 text-right">{r.devis_envoyes}</td>
                                                        <td className="px-4 py-3 text-right font-semibold text-emerald-600">{r.devis_acceptes}</td>
                                                        <td className="px-4 py-3 text-right">
                                                            <Badge className={`${r.taux_transformation >= 30 ? 'bg-emerald-100 text-emerald-700' : r.taux_transformation >= 15 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'} border-0`}>
                                                                {r.taux_transformation}%
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-bold text-[#C9A962]">{fmtCA(r.ca)}</td>
                                                        <td className="px-4 py-3 text-right text-xs text-gray-500">
                                                            {r.delai_moyen_envoi_h != null ? <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{r.delai_moyen_envoi_h}h</span> : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            {/* Total row — period-aware aggregation. Reads from
                                                `totals` which the backend computes after the
                                                attribution pass, so the figures here match the
                                                sum of the rows above. */}
                                            <tfoot className="bg-[#1B365D]/5 border-t-2 border-[#1B365D]/20" data-testid="lb-table-total">
                                                <tr className="font-bold text-[#1B365D]">
                                                    <td className="px-4 py-3 uppercase text-xs tracking-wider">Total</td>
                                                    <td className="px-4 py-3 text-right tabular-nums">{totals?.prospects ?? 0}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums">{totals?.devis_envoyes ?? 0}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{totals?.devis_acceptes ?? 0}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <Badge className="bg-[#1B365D] text-white border-0">
                                                            {totals?.taux_transformation ?? 0}%
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-[#C9A962] tabular-nums">{fmtCA(totals?.ca)}</td>
                                                    <td className="px-4 py-3 text-right text-xs text-gray-500">—</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>

            {/* Drill-down modal : list of devis envoyés / acceptés for the
                clicked user, scoped to the same period as the leaderboard. */}
            {drillUser && (
                <div
                    className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 sm:p-8 overflow-y-auto"
                    onClick={closeDrill}
                    data-testid="lb-drill-overlay"
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <div>
                                <h3 className="text-lg font-bold text-[#1B365D]">{drillUser.user_name}</h3>
                                <p className="text-xs text-gray-500 capitalize">{drillUser.user_role}</p>
                            </div>
                            <button
                                type="button"
                                onClick={closeDrill}
                                className="text-gray-400 hover:text-gray-700 transition-colors"
                                data-testid="lb-drill-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="px-6 pt-4 flex items-center gap-2 border-b border-gray-100">
                            <button
                                type="button"
                                onClick={() => setDrillTab('envoyes')}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${drillTab === 'envoyes' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                data-testid="lb-drill-tab-envoyes"
                            >
                                <Send className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                                Envoyés ({drillData?.totals?.envoyes ?? 0})
                            </button>
                            <button
                                type="button"
                                onClick={() => setDrillTab('acceptes')}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${drillTab === 'acceptes' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                data-testid="lb-drill-tab-acceptes"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                                Acceptés ({drillData?.totals?.acceptes ?? 0})
                            </button>
                            <div className="ml-auto text-xs text-gray-500">
                                CA acceptés : <span className="font-semibold text-[#C9A962]">{fmtCA(drillData?.totals?.ca)}</span>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-4 max-h-[55vh] overflow-y-auto">
                            {drillLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-6 h-6 animate-spin text-[#1B365D]" />
                                </div>
                            ) : (() => {
                                const list = drillTab === 'envoyes' ? drillData?.envoyes : drillData?.acceptes;
                                if (!list || list.length === 0) {
                                    return <p className="text-center text-sm text-gray-400 py-12">Aucun client à afficher sur cette période</p>;
                                }
                                return (
                                    <ul className="divide-y divide-gray-100">
                                        {list.map((c) => (
                                            <li key={c.devis_id} className="py-3" data-testid={`lb-drill-item-${c.devis_id}`}>
                                                <div className="flex items-center justify-between gap-3">
                                                    <div
                                                        className="flex-1 min-w-0 cursor-pointer"
                                                        onClick={() => { if (c.prospect_id) navigate(`/prospects/${c.prospect_id}`); }}
                                                    >
                                                        <p className="font-medium text-sm text-[#1B365D] truncate hover:underline">{c.client_name || '(Sans nom)'}</p>
                                                        <p className="text-xs text-gray-400">{c.date ? new Date(c.date).toLocaleDateString('fr-FR') : '—'}</p>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <div className="text-sm font-semibold text-[#C9A962]">{fmtCA(c.total_ttc)}</div>
                                                        <div className="text-[10px] uppercase tracking-wider text-gray-400">{c.statut?.replace('_', ' ')}</div>
                                                    </div>
                                                    {/* Admin actions */}
                                                    <div className="flex items-center gap-1 shrink-0 ml-2">
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setReassignOpenId(reassignOpenId === c.devis_id ? null : c.devis_id)}
                                                            className="h-7 text-[11px] px-2 border-[#1B365D]/30 text-[#1B365D] hover:bg-[#1B365D]/5"
                                                            data-testid={`lb-drill-reassign-${c.devis_id}`}
                                                            title="Réassigner à un autre utilisateur"
                                                        >
                                                            Réassigner
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleCancel(c)}
                                                            className="h-7 text-[11px] px-2 border-red-300 text-red-600 hover:bg-red-50"
                                                            data-testid={`lb-drill-cancel-${c.devis_id}`}
                                                            title="Annuler (passer en Refusé)"
                                                        >
                                                            Annuler
                                                        </Button>
                                                    </div>
                                                </div>
                                                {reassignOpenId === c.devis_id && (
                                                    <div className="mt-2 pl-3 ml-1 border-l-2 border-[#1B365D]/20 flex items-center gap-2 flex-wrap" data-testid={`lb-drill-reassign-panel-${c.devis_id}`}>
                                                        <span className="text-xs text-gray-500">Attribuer à :</span>
                                                        <select
                                                            className="text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#1B365D]/30"
                                                            onChange={(e) => handleReassign(c, e.target.value)}
                                                            defaultValue=""
                                                            data-testid={`lb-drill-reassign-select-${c.devis_id}`}
                                                        >
                                                            <option value="" disabled>Choisir un utilisateur…</option>
                                                            <option value="auto">↻ Auto (règle visite/listing)</option>
                                                            {assignables.map(u => (
                                                                <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            type="button"
                                                            onClick={() => setReassignOpenId(null)}
                                                            className="text-xs text-gray-400 hover:text-gray-700"
                                                        >
                                                            Annuler
                                                        </button>
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
